import React, { useEffect, useRef, useState } from "react";
import { useSpamGuard } from "@/lib/spamGuard";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, MapPin, ChevronLeft, ChevronRight, Sparkles, CheckCircle2, Star, Plane } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatYMD, formatTimeRange } from "@/lib/dates";
import { toast } from "sonner";
import Mandala from "@/components/Mandala";
import SEO from "@/components/SEO";

// Fallback imagery if admin hasn't set page photos yet.
const FALLBACKS = {
  hero: "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/uvz6fcvb_IMG_8274.jpeg",
  "why-portrait": "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/yipigdji_Holly%202014%20Passions%20Production%20Co-40.jpeg",
  "teaser-1": "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/rpeocub0_SEDONARETREAT-131.png",
  "teaser-2": "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/f2nrwkzu_Page89option.jpg",
};

// "Places We've Been" — Holly's full back-catalogue, oldest-to-newest is just data;
// rendered as a horizontally scrollable timeline of past sacred gatherings.
const PAST_RETREATS = [
  { location: "Bellvue, Colorado",            title: "Mother–Daughter Horse Riding Retreat",                          date: "June 2013" },
  { location: "Bali, Indonesia",              title: "Aviana Aerial Yoga Teacher Training Retreat",                   date: "March 2014" },
  { location: "Lake Atitlán, Guatemala",      title: "Aviana Aerial Yoga Teacher Training",                           date: "August 2017" },
  { location: "Manuel Antonio, Costa Rica",   title: "Sensuality of Self Retreat",                                    date: "July 2018" },
  { location: "South Haven, Michigan",        title: "Cycles of the Feminine — Private Group Retreat",                date: "August 2020" },
  { location: "Sedona, Arizona",              title: "Illuminating the Path of the 13 Medicine Moons",                date: "November 2020" },
  { location: "Saint Simons Island, Georgia", title: "Passionate Flames Couples Retreat",                             date: "March 2021" },
  { location: "Fort Collins, Colorado",       title: "Phoenix Rising Retreat",                                        date: "May 2021" },
  { location: "Saint Simons Island, Georgia", title: "Girlfriend Getaway Retreat",                                    date: "March 2022" },
  { location: "Saint Simons Island, Georgia", title: "Girlfriend Getaway Retreat",                                    date: "October 2022" },
  { location: "Saint Simons Island, Georgia", title: "Passionate Flames Couples Retreat",                             date: "March 2023" },
];

// Google-reviews replacement for testimonials (Holly's actual public reviews).
const GOOGLE_REVIEWS = [
  {
    name: "Sara Schluchter",
    location: "Aviana Aerial Yoga · Google review",
    quote: "I've taken a variety of Holly's offerings and can't recommend her enough! I found Holly to be a highly skilled, dedicated, compassionate and passionate teacher. I so enjoyed her Restorative Aerial Yoga session — Holly creates a space where you can truly let go and reset.",
  },
  {
    name: "Maggie Knaak",
    location: "Aviana Aerial Yoga · Google review",
    quote: "Thank you to Holly Johnson! I had the pleasure of experiencing her restorative aerial yoga with hot stones session. It was immensely relaxing. Holly does an amazing job guiding through the practice with such intention and grace.",
  },
  {
    name: "Jessy Lettieri",
    location: "Aviana Aerial Yoga · Google review",
    quote: "I attended Holly's Cocooned Relaxation and Sound Healing — it was heavenly! I went home later and had an amazing night's rest. I look forward to attending more of Holly's classes; her energy is truly something special.",
  },
];

const STAY_TUNED = [
  { place: "Sedona, Arizona",      timeframe: "November 2026",       tagline: "Red rock awakenings & vortex medicine.",  imgKey: "teaser-1" },
  { place: "Thermopolis, Wyoming", timeframe: "Dates coming soon",    tagline: "Mineral hot springs · sacred sisterhood.", imgKey: "teaser-2" },
];

export default function DestinationRetreats() {
  const [upcoming, setUpcoming] = useState([]);
  const [tIndex, setTIndex] = useState(0);
  const [images, setImages] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [retreatsRes, imgRes] = await Promise.all([
          api.get("/offerings?type=retreat"),
          api.get("/page-images/destination-retreats"),
        ]);
        setUpcoming(retreatsRes.data || []);
        setImages(imgRes.data?.slots || {});
      } catch {/* silent */}
    })();
  }, []);

  const img = (key) => images[key]?.[0] || FALLBACKS[key];
  const galleryPhotos = images["places-gallery"] || [];

  return (
    <div className="bg-aviana-paper" data-testid="destination-retreats-page">
      <SEO
        title="Destination Yoga Retreats with Holly Johnson · Sedona, Wyoming & Beyond"
        description="Sacred destination yoga retreats with Holly Johnson — Sedona red rock vortex retreats, Thermopolis Wyoming mineral hot springs, custom hat bar, soap making, sisterhood circles, and aerial yoga immersions. Small group sizes. Curated stays."
        path="/retreats"
        image={img("hero")}
        keywords="yoga retreat, women's yoga retreat, Sedona yoga retreat, Wyoming hot springs retreat, aerial yoga retreat, sisterhood retreat, sacred retreat, Holly Johnson retreat, destination yoga retreat 2026, custom hat bar retreat"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Aviana Destination Retreats",
          "itemListElement": upcoming.slice(0, 6).map((r, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "item": {
              "@type": "Event",
              "name": r.title,
              "startDate": r.start_date,
              "endDate": r.end_date,
              "location": { "@type": "Place", "name": r.location },
              "organizer": { "@id": "https://avianayoga.com/#organization" }
            }
          }))
        }}
      />
      {/* ──────────────────── HERO ──────────────────── */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-aviana-plumDeep/40 via-aviana-plumDeep/55 to-aviana-ink">
          <img src={img("hero")} alt="" className="w-full h-full object-cover opacity-70 mix-blend-multiply" />
        </div>
        <Mandala size={620} variant="full" className="absolute -top-32 -right-40 text-aviana-goldSoft/15 animate-spin-slow hidden md:block" />
        <Mandala size={420} variant="lotus" className="absolute -bottom-24 -left-32 text-aviana-goldSoft/10 hidden md:block" />
        <div className="container-aviana relative z-10 pt-40 pb-20 text-aviana-cream">
          <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
            <Mandala size={48} variant="lotus" className="text-aviana-goldSoft mb-6" />
            <p className="overline text-aviana-goldSoft mb-5" data-testid="dr-hero-overline">Sacred Destination Retreats</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[96px] leading-[0.96]">
              Destination<br />
              <em className="italic gold-text">Retreats</em>
            </h1>
            <p className="mt-9 text-aviana-cream/85 text-[17px] md:text-[19px] leading-[1.65] max-w-2xl font-serif italic">
              Thoughtfully curated journeys in beautiful corners of the world — where the land, the people, and your own breath guide you home.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-5 justify-center">
              <a href="#upcoming" className="btn-gold" data-testid="dr-hero-cta-upcoming">View upcoming retreats <ArrowRight size={14} /></a>
              <a href="#private" className="text-aviana-goldSoft hover:text-aviana-cream text-[12px] tracking-[0.22em] uppercase">↓ Plan a private retreat</a>
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────── WELCOME ──────────────────── */}
      <section className="py-28 md:py-36 text-center relative overflow-hidden" data-testid="dr-welcome">
        <Mandala size={680} variant="rose" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-aviana-gold opacity-[0.07] animate-spin-slow" />
        <div className="container-aviana relative max-w-3xl">
          <div className="inline-flex items-center gap-2 mb-7">
            {[...Array(4)].map((_, i) => (
              <span key={i} className="text-aviana-plum text-[18px]" style={{ opacity: 0.4 + i * 0.15 }}>✦</span>
            ))}
          </div>
          <h2 className="font-display text-5xl md:text-7xl text-aviana-ink leading-[1.02]">
            Welcome, <em className="italic gold-text">Beautiful Being.</em>
          </h2>
          <p className="mt-9 text-aviana-inkSoft text-[17px] leading-[1.85] font-serif italic max-w-2xl mx-auto">
            You've arrived here for a reason. Maybe you're craving stillness. Maybe a fierce remembering. Maybe you simply want to spend a week barefoot in sacred company. Whatever brought you — you are seen.
          </p>
        </div>
      </section>

      {/* ──────────────────── WHY RETREAT ──────────────────── */}
      <section className="py-24 md:py-28 bg-aviana-cream/40 relative" data-testid="dr-why-retreat">
        <div className="container-aviana grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
          <div className="lg:col-span-6 lg:order-2">
            <div className="relative">
              <img src={img("why-portrait")} alt="Holly Johnson" className="rounded-[2.5rem] aspect-[4/5] object-cover w-full ring-1 ring-aviana-border shadow-xl" />
              <Mandala size={100} variant="lotus" className="absolute -bottom-6 -left-6 text-aviana-gold/80" />
            </div>
          </div>
          <div className="lg:col-span-6 lg:order-1">
            <p className="overline mb-4">The Aviana Difference</p>
            <h2 className="font-display text-4xl md:text-6xl text-aviana-ink leading-[1.02]">
              Why Retreat<br />
              <em className="italic gold-text">with Aviana?</em>
            </h2>
            <div className="mt-9 space-y-5 text-aviana-inkSoft text-[16px] leading-[1.85]">
              <p>At Aviana, each retreat is thoughtfully designed around a core intention — serving as the foundation for a truly transformative experience. We leverage extensive global travel expertise to handle every detail, so you can fully immerse yourself in the journey without the stress of planning.</p>
              <p>Our mission is simple: <strong className="text-aviana-plum">we take care of the logistics so you can focus on what truly matters</strong> — connecting with yourself, the destination, and your sisters. While every retreat offers a unique platform of meaningful intentions, it is the incredible community of participants that ultimately shapes the experience — creating an atmosphere that is peaceful, playful, and truly magical.</p>
              <p className="font-serif italic text-aviana-plum text-[18px] leading-[1.55]">Join us and be part of something special. Are you ready to spread your wings and embark on an unforgettable adventure?</p>
            </div>
            <div className="mt-10">
              <a href="#upcoming" className="btn-primary" data-testid="dr-why-cta">Discover upcoming retreats <ArrowRight size={14} /></a>
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────── PLACES WE'VE BEEN ──────────────────── */}
      <PlacesWeveBeen photos={galleryPhotos} />

      {/* ──────────────────── UPCOMING ──────────────────── */}
      <section id="upcoming" className="py-24 md:py-28 bg-aviana-plum/5" data-testid="dr-upcoming">
        <div className="container-aviana">
          <div className="text-center mb-14">
            <p className="overline mb-4">On the horizon</p>
            <h2 className="font-display text-5xl md:text-7xl text-aviana-ink leading-[1.02]">
              Retreat <em className="italic gold-text">Experiences.</em>
            </h2>
          </div>
          {upcoming.length === 0 ? (
            <p className="text-center text-aviana-muted" data-testid="no-upcoming-retreats">The next retreats are being dreamed up beneath the new moon — check back soon.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {upcoming.map((r) => <RetreatCard key={r.id} offering={r} />)}
            </div>
          )}
        </div>
      </section>

      {/* ──────────────────── STAY TUNED ──────────────────── */}
      <section className="py-28 bg-aviana-ink text-aviana-cream relative overflow-hidden" data-testid="dr-stay-tuned">
        <Mandala size={780} variant="full" className="absolute -left-40 -top-20 text-aviana-gold/10 animate-spin-slow" />
        <div className="container-aviana relative">
          <div className="text-center mb-14">
            <p className="overline mb-4 text-aviana-goldSoft">Whispers of what's coming</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[1.02]">Stay <em className="italic gold-text">Tuned.</em></h2>
            <p className="mt-6 text-aviana-cream/70 text-[15px] max-w-xl mx-auto">Destinations being woven for the seasons ahead.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {STAY_TUNED.map((s) => (
              <TeaserCard key={s.place} image={img(s.imgKey)} place={s.place} timeframe={s.timeframe} tagline={s.tagline} />
            ))}
          </div>
          <p className="mt-12 text-center text-aviana-cream/60 text-[13px]">
            Want early access? <a href="#newsletter" className="text-aviana-goldSoft underline underline-offset-4 hover:text-aviana-cream">Join the moon-letter</a>.
          </p>
        </div>
      </section>

      {/* ──────────────────── TESTIMONIALS (Google Reviews) ──────────────────── */}
      <section className="py-28 md:py-32 bg-aviana-surface" data-testid="dr-testimonials">
        <div className="container-aviana max-w-4xl">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-1 text-aviana-gold mb-3">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
            </div>
            <p className="overline-plum mb-3">Excellent · Based on 13 Google Reviews</p>
            <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.02]">
              What People <em className="italic gold-text">Are Saying.</em>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {GOOGLE_REVIEWS.map((t, i) => (
              <figure key={i} className="bg-aviana-cream rounded-2xl p-7 border border-aviana-border" data-testid={`review-${i}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-aviana-plum/15 text-aviana-plum flex items-center justify-center font-display text-lg">
                    {t.name.charAt(0)}
                  </div>
                  <div className="flex items-center gap-1 text-aviana-gold">
                    {[...Array(5)].map((_, j) => <Star key={j} size={11} fill="currentColor" />)}
                  </div>
                </div>
                <blockquote className="font-serif text-[14px] leading-[1.7] text-aviana-inkSoft italic">"{t.quote}"</blockquote>
                <figcaption className="mt-5 pt-4 border-t border-aviana-border/70">
                  <p className="font-display text-base text-aviana-ink">{t.name}</p>
                  <p className="text-[10px] tracking-[0.18em] uppercase text-aviana-muted/80 mt-0.5">{t.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>
          <p className="text-center text-[11px] text-aviana-muted/80 mt-10 tracking-[0.18em] uppercase">From Google · @avianayoga</p>
        </div>
      </section>

      {/* ──────────────────── PRIVATE RETREAT (now ABOVE newsletter, redesigned) ──────────────────── */}
      <PrivateRetreatSection />

      {/* ──────────────────── NEWSLETTER ──────────────────── */}
      <NewsletterCTA />
    </div>
  );
}

/* ───────── Places We've Been (horizontal scroller) ───────── */
function PlacesWeveBeen({ photos }) {
  const scrollerRef = useRef(null);
  const scrollBy = (dx) => scrollerRef.current?.scrollBy({ left: dx, behavior: "smooth" });

  return (
    <section className="py-24 md:py-32 relative overflow-hidden" data-testid="dr-places">
      <Mandala size={520} variant="full" className="absolute -right-32 top-12 text-aviana-gold opacity-[0.06] animate-spin-slow hidden md:block" />
      <div className="container-aviana relative">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="overline mb-4">Footprints across the map</p>
            <h2 className="font-display text-5xl md:text-7xl text-aviana-ink leading-[1.02]">
              Places <em className="italic gold-text">We've Been.</em>
            </h2>
            <p className="mt-5 text-aviana-muted text-[15px] leading-[1.7]">A scrollable timeline of sacred gatherings — each one a thread in the Aviana sisterhood weaving.</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => scrollBy(-360)} aria-label="Scroll left" className="w-11 h-11 rounded-full bg-white border border-aviana-border text-aviana-inkSoft hover:border-aviana-plum hover:text-aviana-plum transition-colors flex items-center justify-center" data-testid="places-prev">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => scrollBy(360)} aria-label="Scroll right" className="w-11 h-11 rounded-full bg-white border border-aviana-border text-aviana-inkSoft hover:border-aviana-plum hover:text-aviana-plum transition-colors flex items-center justify-center" data-testid="places-next">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <div ref={scrollerRef} className="overflow-x-auto scrollbar-hidden pb-4">
        <div className="flex gap-5 px-6 md:px-12 min-w-min">
          {PAST_RETREATS.map((r, i) => (
            <article
              key={`${r.title}-${r.date}-${i}`}
              className="bg-white rounded-[1.75rem] border border-aviana-border p-7 md:p-8 relative overflow-hidden group flex-shrink-0 w-[300px] md:w-[340px]"
              data-testid={`past-retreat-${i}`}
            >
              <Mandala size={70} variant="rose" className="absolute -top-3 -right-3 text-aviana-gold opacity-30 group-hover:opacity-60 transition-opacity" />
              {photos[i] ? (
                <div className="-mx-7 md:-mx-8 -mt-7 md:-mt-8 mb-5 aspect-[4/3] overflow-hidden">
                  <img src={photos[i]} alt={r.title} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="-mx-7 md:-mx-8 -mt-7 md:-mt-8 mb-5 aspect-[4/3] bg-gradient-to-br from-aviana-plum/15 via-aviana-cream to-aviana-gold/15 flex items-center justify-center">
                  <Plane size={28} className="text-aviana-plum/40 -rotate-12" />
                </div>
              )}
              <div className="flex items-center gap-2 text-aviana-plum mb-3">
                <MapPin size={13} />
                <span className="text-[10px] tracking-[0.22em] uppercase">{r.location}</span>
              </div>
              <h3 className="font-display text-xl md:text-[22px] text-aviana-ink leading-[1.15]">{r.title}</h3>
              <p className="mt-3 text-aviana-muted text-[11px] tracking-[0.2em] uppercase">{r.date}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────── Sub-components ───────── */
function RetreatCard({ offering }) {
  // Saddle & Soul has its own dedicated landing at /retreats/saddle-and-soul
  // Solstice has its own at /retreats/solstice-in-the-saddle. All other offerings
  // route to the generic /offering/<slug> detail page.
  const slug = (offering.slug || "").toLowerCase();
  const destination = slug.includes("solstice-in-the-saddle")
    ? "/retreats/solstice-in-the-saddle"
    : slug.includes("saddle-and-soul")
    ? "/retreats/saddle-and-soul"
    : `/offering/${offering.slug}`;

  return (
    <Link to={destination} className="group block bg-white rounded-[2rem] border border-aviana-border overflow-hidden hover:shadow-lg transition-shadow" data-testid={`upcoming-retreat-${offering.slug}`}>
      <div className="aspect-[4/3] overflow-hidden">
        <img src={offering.image_url} alt={offering.title} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
      </div>
      <div className="p-7">
        <div className="flex items-center gap-2 text-aviana-muted text-[11px] tracking-[0.2em] uppercase mb-3">
          <Calendar size={12} className="text-aviana-gold" />
          {offering.start_date && formatYMD(offering.start_date, { month: "long", day: "numeric", year: "numeric" })}
          {formatTimeRange(offering.start_time, offering.end_time) && (
            <span data-testid={`retreat-card-time-${offering.slug}`}>· {formatTimeRange(offering.start_time, offering.end_time)}</span>
          )}
        </div>
        <h3 className="font-display text-2xl text-aviana-ink leading-[1.15] group-hover:text-aviana-plum transition-colors">{offering.title}</h3>
        <p className="mt-2 text-aviana-muted text-[13px]">{offering.location}</p>
        <div className="mt-5 flex items-end justify-between">
          <div>
            <p className="font-display text-2xl text-aviana-ink">${offering.price_usd?.toLocaleString()}</p>
            {offering.sisterhood_price_usd && (
              <p className="text-[11px] tracking-[0.18em] uppercase text-aviana-goldDeep mt-1">or ${offering.sisterhood_price_usd.toLocaleString()} sisterhood</p>
            )}
          </div>
          <span className="text-[11px] tracking-[0.22em] uppercase text-aviana-gold group-hover:tracking-[0.3em] transition-all">Discover →</span>
        </div>
        {offering.spots_left <= 5 && offering.spots_left < 9999 && offering.spots_left > 0 && (
          <p className="mt-4 text-[11px] tracking-[0.18em] uppercase text-aviana-plum">Only {offering.spots_left} {offering.spots_left === 1 ? "spot" : "spots"} remain{offering.spots_left === 1 ? "s" : ""}</p>
        )}
      </div>
    </Link>
  );
}

function TeaserCard({ image, place, timeframe, tagline }) {
  return (
    <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] group" data-testid={`teaser-${place.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <img src={image} alt={place} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-aviana-ink/90 via-aviana-ink/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-7 text-aviana-cream">
        <p className="overline text-aviana-goldSoft mb-2">{timeframe}</p>
        <h3 className="font-display text-3xl md:text-4xl leading-[1.05]">{place}</h3>
        <p className="mt-2 text-aviana-cream/80 text-[13px] italic font-serif">{tagline}</p>
      </div>
    </div>
  );
}

/* ───────── Private Retreat Section (redesigned, full-bleed) ───────── */
function PrivateRetreatSection() {
  const { honeypot, guard } = useSpamGuard();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    party_size: 4, retreat_type: "local",
    preferred_dates: "", retreat_length_days: 3, message: "",
  });

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const noticeDays = form.retreat_type === "local" ? 60 : 90;

  const submit = async (e) => {
    e.preventDefault();
    if (form.party_size < 4) { toast.error("Custom retreats require a minimum of 4 people."); return; }
    setSubmitting(true);
    try {
      await api.post("/inquiries/private-retreat", { ...form, ...guard() });
      setSubmitted(true);
      toast.success("Inquiry received — Holly will be in touch soon.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <section id="private" className="py-32 relative overflow-hidden" data-testid="dr-private-section">
      <Mandala size={900} variant="full" className="absolute -right-60 top-1/2 -translate-y-1/2 text-aviana-gold opacity-[0.05] animate-spin-slow hidden md:block" />
      <Mandala size={420} variant="lotus" className="absolute -left-32 -bottom-20 text-aviana-plum opacity-[0.06] hidden md:block" />
      <div className="container-aviana max-w-6xl relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left — pitch */}
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <p className="overline-plum mb-4">Custom · Private · For your circle</p>
            <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.02]">
              Co-create<br />
              a <em className="italic gold-text">private retreat.</em>
            </h2>
            <p className="mt-7 text-aviana-inkSoft text-[16px] leading-[1.8] font-serif">
              Gather a circle of 4 or more sisters and Holly will craft something just for you — moon ceremonies, sound healings, aerial intensives, mother–daughter weekends, business off-sites with breathwork built in. Local options near Sugar Grove, IL or destination retreats anywhere we dream up.
            </p>
            <ul className="mt-8 space-y-3 text-aviana-inkSoft text-[14px]">
              <Bullet>Minimum 4 women · no maximum — bring the whole tribe</Bullet>
              <Bullet>Holly designs the intention, programming, and flow</Bullet>
              <Bullet>Local retreats: <strong>60+ days notice</strong> · Destinations: <strong>3+ months</strong></Bullet>
              <Bullet>Everything from accommodations to meals — we handle it</Bullet>
            </ul>
            {!open && !submitted && (
              <button onClick={() => setOpen(true)} className="btn-primary mt-10 inline-flex items-center gap-2 !text-[12px]" data-testid="open-private-retreat-form">
                Start a private retreat inquiry <ArrowRight size={14} />
              </button>
            )}
          </div>

          {/* Right — form (or success state) */}
          <div className="lg:col-span-7">
            <div className="bg-aviana-cream/50 border border-aviana-border rounded-[2rem] p-8 md:p-10 relative">
              <div className="absolute -top-5 -right-5 w-14 h-14 rounded-full bg-aviana-gold flex items-center justify-center text-aviana-ink shadow-lg">
                <Sparkles size={20} />
              </div>
              {submitted ? (
                <div className="text-center py-10" data-testid="private-retreat-submitted">
                  <CheckCircle2 size={36} className="text-aviana-gold mx-auto mb-4" />
                  <h3 className="font-display text-3xl text-aviana-ink">Beautiful — it's in Holly's hands.</h3>
                  <p className="mt-3 text-aviana-muted text-[14px] max-w-md mx-auto leading-[1.7]">She personally reads each inquiry and will reach out within 2–3 days to begin shaping this with you.</p>
                </div>
              ) : !open ? (
                <div className="text-center py-12">
                  <h3 className="font-display text-3xl text-aviana-ink">Ready to begin?</h3>
                  <p className="mt-3 text-aviana-muted text-[14px] max-w-md mx-auto leading-[1.7]">Tell us about your circle and Holly will reach out to weave the experience with you.</p>
                  <button onClick={() => setOpen(true)} className="btn-gold mt-8 inline-flex items-center gap-2 !text-[12px]" data-testid="open-private-retreat-form-cta">
                    Start your inquiry <ArrowRight size={14} />
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="private-retreat-form">
            {honeypot}
                  <Input label="Your name *" value={form.name} onChange={(v) => update({ name: v })} testid="pr-name" />
                  <Input label="Email *" type="email" value={form.email} onChange={(v) => update({ email: v })} testid="pr-email" />
                  <Input label="Phone (optional)" value={form.phone} onChange={(v) => update({ phone: v })} testid="pr-phone" />
                  <Input label="Party size (min 4) *" type="number" min={4} value={form.party_size} onChange={(v) => update({ party_size: Number(v) || 0 })} testid="pr-party-size" />
                  <label className="block sm:col-span-2">
                    <span className="overline-plum mb-2 block">Local or destination? *</span>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { v: "local", l: "Local · Sugar Grove, IL area" },
                        { v: "destination", l: "Destination · anywhere" },
                      ].map((opt) => (
                        <button type="button" key={opt.v} onClick={() => update({ retreat_type: opt.v })} className={`text-left px-4 py-3.5 rounded-xl text-[13px] border transition-colors ${form.retreat_type === opt.v ? "bg-aviana-ink text-aviana-cream border-aviana-ink" : "border-aviana-border bg-white text-aviana-inkSoft hover:border-aviana-plum"}`} data-testid={`pr-type-${opt.v}`}>
                          {opt.l}
                        </button>
                      ))}
                    </div>
                    <p className="text-[12px] text-aviana-muted mt-1.5">Requires {noticeDays}+ days notice.</p>
                  </label>
                  <Input label="Length (days) *" type="number" min={1} value={form.retreat_length_days} onChange={(v) => update({ retreat_length_days: Number(v) || 0 })} testid="pr-length" />
                  <Input label="Preferred dates *" placeholder="e.g. last weekend of Sept" value={form.preferred_dates} onChange={(v) => update({ preferred_dates: v })} testid="pr-dates" />
                  <label className="sm:col-span-2 block">
                    <span className="overline-plum mb-2 block">Tell Holly your vision</span>
                    <textarea rows={4} value={form.message} onChange={(e) => update({ message: e.target.value })} placeholder="What kind of energy, themes, or experiences are you hoping for? Any ages or mobility needs in the group?" className="w-full bg-white border border-aviana-border rounded-xl px-4 py-3 text-[14px] text-aviana-ink leading-[1.55] focus:outline-none focus:border-aviana-plum focus:ring-1 focus:ring-aviana-plum/30" data-testid="pr-message" />
                  </label>
                  <div className="sm:col-span-2 pt-2">
                    <button type="submit" disabled={submitting} className="btn-primary w-full !py-3.5 !text-[12px] tracking-[0.22em] uppercase" data-testid="submit-private-retreat">
                      {submitting ? "Sending…" : "Send my inquiry"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-aviana-gold flex-shrink-0" />
      <span className="leading-[1.65]">{children}</span>
    </li>
  );
}

function NewsletterCTA() {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [interests, setInterests] = useState({ aerial_training: false, destination_retreats: true });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Please share your email."); return; }
    setBusy(true);
    try {
      await api.post("/newsletter", {
        email: form.email, name: form.name, phone: form.phone,
        source: "destination-retreats",
        interests: Object.entries(interests).filter(([, v]) => v).map(([k]) => k),
      });
      setDone(true);
      toast.success("Welcome to the sisterhood. Look for moon-cycle letters in your inbox.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not subscribe. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <section id="newsletter" className="py-28 bg-aviana-paper relative overflow-hidden" data-testid="dr-newsletter">
      <Mandala size={520} variant="lotus" className="absolute -left-32 -bottom-20 text-aviana-gold opacity-[0.08] hidden md:block" />
      <div className="container-aviana max-w-3xl relative">
        <div className="text-center mb-10">
          <Sparkles size={22} className="text-aviana-gold mx-auto mb-4" />
          <p className="overline-plum mb-3">Be the first to know</p>
          <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">
            Join the <em className="italic gold-text">moon-letter.</em>
          </h2>
          <p className="mt-5 text-aviana-muted text-[15px] max-w-xl mx-auto leading-[1.7]">Quiet, monthly notes from Holly. Early access to retreats, training news, free practices, and the occasional poem.</p>
        </div>

        {done ? (
          <div className="bg-white border border-aviana-gold/30 rounded-2xl p-10 text-center" data-testid="dr-newsletter-done">
            <CheckCircle2 size={28} className="text-aviana-gold mx-auto mb-3" />
            <h3 className="font-display text-2xl text-aviana-ink">Welcome, sister.</h3>
            <p className="mt-2 text-aviana-muted text-[14px]">Your first letter will arrive at the next new moon.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-aviana-border rounded-[2rem] p-7 md:p-10 grid sm:grid-cols-2 gap-4" data-testid="dr-newsletter-form">
            <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="nl-name" />
            <Input label="Email *" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="nl-email" />
            <Input label="Phone (optional)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="nl-phone" wide />
            <div className="sm:col-span-2">
              <p className="overline-plum mb-3">What calls to you?</p>
              <div className="flex flex-wrap gap-3">
                <Toggle label="Aerial Yoga Teacher Trainings" checked={interests.aerial_training} onChange={(v) => setInterests({ ...interests, aerial_training: v })} testid="interest-aerial" />
                <Toggle label="Destination Retreats" checked={interests.destination_retreats} onChange={(v) => setInterests({ ...interests, destination_retreats: v })} testid="interest-retreats" />
              </div>
            </div>
            <div className="sm:col-span-2 pt-2">
              <button type="submit" disabled={busy} className="btn-primary w-full !py-3.5 !text-[12px] tracking-[0.22em] uppercase" data-testid="dr-newsletter-submit">
                {busy ? "Subscribing…" : "Subscribe to moon-letters"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function Input({ label, value, onChange, type = "text", required, placeholder, testid, wide, min }) {
  return (
    <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
      <span className="overline-plum mb-2 block">{label}</span>
      <input type={type} value={value} required={required} placeholder={placeholder} min={min} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-aviana-cream/40 border border-aviana-border rounded-xl px-4 py-3 text-[14px] text-aviana-ink focus:outline-none focus:border-aviana-plum focus:ring-1 focus:ring-aviana-plum/30" />
    </label>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid} className={`px-5 py-2.5 rounded-full text-[12px] tracking-[0.16em] uppercase border transition-all ${checked ? "bg-aviana-ink text-aviana-cream border-aviana-ink" : "border-aviana-border text-aviana-inkSoft hover:border-aviana-plum"}`}>
      {checked ? "✓ " : ""}{label}
    </button>
  );
}
